import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "@/store";

export type JanusPublisherRole = "main-camera" | "second-camera" | "screen";

export type JanusPublisher = {
  id: string;
  display: string;
  audio_codec?: string;
  video_codec?: string;
};

const EMPTY_PUBLISHERS: JanusPublisher[] = [];

type WebinarPublishersState = {
  publishers: JanusPublisher[];
  updatedAt: number;
};

type JanusPublishersState = {
  /**
   * Keyed by webinar_id (room context).
   * This allows multiple ProctoringLists/rooms to coexist without clobbering each other.
   */
  byWebinarId: Record<number, WebinarPublishersState | undefined>;
};

const initialState: JanusPublishersState = {
  byWebinarId: {},
};

type SetPublishersPayload = {
  webinarId: number;
  publishers: JanusPublisher[];
};

type UpsertPublishersPayload = {
  webinarId: number;
  publishers: JanusPublisher[];
};

type RemovePublisherPayload = {
  webinarId: number;
  publisherId: string;
};

export const janusPublishersSlice = createSlice({
  name: "janusPublishers",
  initialState,
  reducers: {
    setPublishers: (state, action: PayloadAction<SetPublishersPayload>) => {
      const { webinarId, publishers } = action.payload;
      state.byWebinarId[webinarId] = {
        publishers,
        updatedAt: Date.now(),
      };
    },

    upsertPublishers: (state, action: PayloadAction<UpsertPublishersPayload>) => {
      const { webinarId, publishers } = action.payload;

      const prev = state.byWebinarId[webinarId]?.publishers ?? [];
      const map = new Map<string, JanusPublisher>(prev.map((p) => [p.id, p]));

      let changed = false;
      for (const p of publishers) {
        const existing = map.get(p.id);
        if (
          !existing ||
          existing.display !== p.display ||
          existing.audio_codec !== p.audio_codec ||
          existing.video_codec !== p.video_codec
        ) {
          map.set(p.id, p);
          changed = true;
        }
      }

      if (!changed) return;

      state.byWebinarId[webinarId] = {
        publishers: Array.from(map.values()),
        updatedAt: Date.now(),
      };
    },

    removePublisher: (state, action: PayloadAction<RemovePublisherPayload>) => {
      const { webinarId, publisherId } = action.payload;
      const prev = state.byWebinarId[webinarId]?.publishers ?? [];
      state.byWebinarId[webinarId] = {
        publishers: prev.filter((p) => p.id !== publisherId),
        updatedAt: Date.now(),
      };
    },

    clearWebinarPublishers: (state, action: PayloadAction<{ webinarId: number }>) => {
      delete state.byWebinarId[action.payload.webinarId];
    },
  },
});

export const {
  setPublishers,
  upsertPublishers,
  removePublisher,
  clearWebinarPublishers,
} = janusPublishersSlice.actions;

export default janusPublishersSlice.reducer;

export const selectJanusPublishersByWebinarId = (
  state: RootState,
  webinarId: number
): JanusPublisher[] => state.janusPublishers.byWebinarId[webinarId]?.publishers ?? EMPTY_PUBLISHERS;